"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const wp_query_params = {
  per_page: 8,
  product_cat: [17]
};
const wp_query = new WP_Query(wp_query_params);
function FeaturedSystemsCarousel() {
  const {
    products,
    loading,
    error
  } = useEcommerceProducts();
  if (loading) {
    return <div className="w-full overflow-hidden">
        <div className="flex -ml-4">
          {[...Array(4)].map((_, i) => <div key={i} data-index={i} className="min-w-0 shrink-0 grow-0 basis-full pl-4 md:basis-1/2 lg:basis-1/3 xl:basis-1/4">
              <div className="h-[400px] rounded-2xl bg-muted/50 animate-pulse" />
            </div>)}
        </div>
      </div>;
  }
  if (error || products.length === 0) {
    return <div className="text-center py-12 text-muted-foreground font-sans">
        <p>Aktuell sind keine neuen Schätze verfügbar. Schau bald wieder vorbei!</p>
      </div>;
  }
  return <Carousel opts={{
    align: "start",
    loop: true
  }} className="w-full">
      <CarouselContent className="-ml-4 md:-ml-6">
        {products.map((product, i) => <CarouselItem key={product.id} data-index={i} className="pl-4 md:pl-6 basis-full sm:basis-1/2 md:basis-1/2 lg:basis-1/3 xl:basis-1/4">
            <Card className="group relative h-full flex flex-col overflow-hidden border-border/60 bg-card hover:border-secondary/40 hover:shadow-xl transition-all duration-500 rounded-2xl">
              {}
              <div className="relative aspect-[4/3] bg-muted/10 p-6 flex items-center justify-center overflow-hidden">
                <Link to={product.link} className="absolute inset-0 z-10">
                  <span className="sr-only">Ansehen {product.title}</span>
                </Link>
                {product.featured_image_url ? <img src={product.featured_image_url} alt={product.title} className="object-contain w-full h-full transition-transform duration-700 group-hover:scale-110 mix-blend-multiply dark:mix-blend-normal" loading="lazy" /> : <div className="w-full h-full bg-muted/20 rounded-lg flex items-center justify-center">
                    <span className="text-muted-foreground font-sans text-sm">Kein Bild</span>
                  </div>}
                
                {}
                <Badge variant="secondary" className="absolute top-4 left-4 z-20 bg-secondary/80 text-secondary-foreground backdrop-blur-md shadow-sm font-default font-semibold tracking-wide">
                  {product.customFields?.condition ? product.customFields.condition : "Lieblingsstück"}
                </Badge>
              </div>

              {}
              <CardContent className="flex-1 p-6 flex flex-col bg-card relative z-20">
                <Link to={product.link} className="hover:underline decoration-secondary underline-offset-4">
                  <h3 className="font-default font-bold text-xl text-foreground mb-2 line-clamp-2 leading-tight">
                    {product.title}
                  </h3>
                </Link>
                
                {product.short_description && <div className="text-sm text-muted-foreground font-sans line-clamp-2 mb-4 [&>p]:mb-0" dangerouslySetInnerHTML={{
              __html: product.short_description
            }} />}

                <div className="mt-auto pt-4 flex items-center justify-between">
                  <span className="font-sans font-bold text-2xl text-secondary">
                    {product.price?.toFixed(2) || "0.00"}€
                  </span>
                </div>

                {}
                <div className="absolute inset-x-0 bottom-0 p-6 bg-card translate-y-full opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500 ease-out z-30 border-t border-border/30 shadow-lg">
                  <AddToCartButton productId={product.id} className="w-full" buttonClassName="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-default font-semibold shadow-md rounded-full" text="In den Warenkorb" showQuantity={false} />
                </div>
              </CardContent>
            </Card>
          </CarouselItem>)}
      </CarouselContent>
      
      {}
      <div className="flex items-center justify-center gap-4 mt-10">
        <CarouselPrevious className="static translate-y-0 translate-x-0 bg-background border-border hover:bg-accent hover:text-accent-foreground size-12 rounded-lg shadow-sm" />
        <CarouselNext className="static translate-y-0 translate-x-0 bg-background border-border hover:bg-accent hover:text-accent-foreground size-12 rounded-lg shadow-sm" />
      </div>
    </Carousel>;
}
export default function FeaturedHvacSystems() {
  return <section data-section-id="223015" id="neue-schätze-im-sortiment" className="py-24 bg-background text-foreground relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-1/2 bg-accent/40 -z-10" />
      
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <h2 className="font-default text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4 tracking-tight">
              Neue Schätze im Sortiment
            </h2>
            <p className="font-sans text-lg text-muted-foreground">
              Entdecke unsere liebevoll ausgewählten Einzelstücke – von individuellen Windeltorten bis hin zu nachhaltigen Räuberkisten.
            </p>
          </div>
          <div className="shrink-0">
            <Link to="/ShopPage" className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 text-secondary hover:text-secondary/80 underline-offset-4 hover:underline">
              Alle Schätze stöbern &rarr;
            </Link>
          </div>
        </div>

        <EcommerceProductsProvider wp_query={{
        per_page: 8,
        product_cat: [17]
      }}>
          <FeaturedSystemsCarousel />
        </EcommerceProductsProvider>
      </div>
    </section>;
}